import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Scissors, LogOut, Home, Menu, X, Bell, Check } from "lucide-react";

function NotifBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = items.filter((n) => !n.read).length;
  const markRead = async (id) => { await api.put(`/notifications/${id}/read`); load(); };
  const markAll = async () => { await api.put("/notifications/read-all"); load(); };

  return (
    <div className="relative" ref={wrapRef}>
      <button data-testid="admin-notif-bell" onClick={() => setOpen((o) => !o)}
              className="relative w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors duration-200">
        <Bell className="w-4 h-4 text-gray-300" />
        {unread > 0 && (
          <span data-testid="admin-notif-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-black text-[0.6rem] font-bold flex items-center justify-center">{unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="admin-notif-dropdown" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-[#141414] border border-white/10 rounded-2xl shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="label-caps">Notifications</span>
            {unread > 0 && <button data-testid="admin-notif-mark-all" onClick={markAll} className="text-[#D4AF37] text-xs hover:underline">Mark all read</button>}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 text-sm">No notifications yet.</div>
          ) : (
            items.slice(0, 30).map((n) => (
              <div key={n.id} data-testid={`admin-notif-${n.id}`} className={`px-4 py-3 border-b border-white/5 flex justify-between gap-3 ${n.read ? "" : "bg-[#D4AF37]/5"}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  <p className="text-gray-400 text-xs mt-1 whitespace-pre-line">{n.message}</p>
                </div>
                {!n.read && <button data-testid={`admin-notif-read-${n.id}`} onClick={() => markRead(n.id)} className="text-[#D4AF37] shrink-0"><Check className="w-4 h-4" /></button>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function DashboardShell({ title, nav, active, onNav, children, testid, identity, onLogout }) {
  const ctx = useAuth();
  const user = identity !== undefined ? identity : ctx.user;
  const doLogout = onLogout || ctx.logout;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isStaff = user?.role === "admin" || user?.role === "barber";

  return (
    <div data-testid={testid} className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 bg-[#141414] border-r border-white/10 flex-col transition-transform duration-300 ${open ? "flex translate-x-0" : "hidden lg:flex"}`}>
        <Link to="/" className="flex items-center gap-3 px-6 h-20 border-b border-white/10">
          <span className="w-9 h-9 rounded-full border border-[#D4AF37]/50 flex items-center justify-center"><Scissors className="w-4 h-4 text-[#D4AF37]" /></span>
          <div className="leading-none"><div className="font-display text-lg">Who's Next</div><div className="label-caps text-[0.5rem]">{title}</div></div>
        </Link>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <button
              key={n.key}
              data-testid={`dash-nav-${n.key}`}
              onClick={() => { onNav(n.key); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors duration-200 ${
                active === n.key ? "bg-[#D4AF37] text-black font-medium" : "text-gray-400 hover:text-white hover:bg-white/5"}`}
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-1">
          <Link data-testid="dash-back-to-site" to="/" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors duration-200"><Home className="w-4 h-4" /> Back to site</Link>
          <button data-testid="dash-logout" onClick={() => { doLogout(); navigate("/"); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors duration-200"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="h-20 border-b border-white/10 flex items-center justify-between px-5 sm:px-8 sticky top-0 bg-[#0A0A0A]/80 backdrop-blur-xl z-20">
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
          <h1 className="font-display text-2xl">{nav.find((n) => n.key === active)?.label}</h1>
          <div className="flex items-center gap-3">
            {isStaff && <NotifBell />}
            <div className="text-right hidden sm:block"><div className="text-sm">{user?.name}</div><div className="label-caps text-[0.5rem]">{user?.role}</div></div>
            <div className="w-9 h-9 rounded-full bg-[#D4AF37] text-black flex items-center justify-center font-medium">{user?.name?.[0]}</div>
          </div>
        </div>
        <div className="p-5 sm:p-8">{children}</div>
      </div>
    </div>
  );
}
